(() => {
  'use strict';

  if (window.YYLocalCurrency?.initialized) return;

  const CARD_SELECTOR = '[data-yy-card-local-price]';
  const PRODUCT_SELECTOR = '[data-yy-product-local-price]';
  const TARGET_SELECTOR = `${CARD_SELECTOR}, ${PRODUCT_SELECTOR}`;

  const COUNTRY_TO_CURRENCY = {
    GE: 'GEL',
    GB: 'GBP',
    CH: 'CHF',
    NO: 'NOK',
    SE: 'SEK',
    DK: 'DKK',
    PL: 'PLN',
    CZ: 'CZK',
    HU: 'HUF',
    RO: 'RON',
    AU: 'AUD',
    NZ: 'NZD',
    US: 'USD',
    CA: 'CAD'
  };

  const FX_URLS = [
    'https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/eur.min.json',
    'https://latest.currency-api.pages.dev/v1/currencies/eur.min.json'
  ];

  const CACHE_TTL = 12 * 60 * 60 * 1000;
  const variantsCache = new WeakMap();

  let contextPromise = null;
  let observer = null;
  let queuedFrame = null;

  const numberFormatter = new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 0
  });

  function getCountry() {
    return String(window.YY_LOCAL_CURRENCY_CONFIG?.country || '')
      .trim()
      .toUpperCase();
  }

  function getCurrency(country) {
    return COUNTRY_TO_CURRENCY[country] || null;
  }

  function getCacheKey(currency) {
    return `yy_fx_eur_${currency}_v3`;
  }

  function readCache(currency) {
    try {
      const raw = localStorage.getItem(getCacheKey(currency));
      if (!raw) return null;

      const parsed = JSON.parse(raw);
      const rate = Number(parsed.rate);
      const savedAt = Number(parsed.savedAt || 0);

      if (!Number.isFinite(rate) || rate <= 0 || !savedAt) return null;
      if (Date.now() - savedAt >= CACHE_TTL) return null;

      return rate;
    } catch (error) {
      return null;
    }
  }

  function writeCache(currency, rate) {
    try {
      localStorage.setItem(
        getCacheKey(currency),
        JSON.stringify({ rate, savedAt: Date.now() })
      );
    } catch (error) {
      /* Continue without caching when storage is unavailable. */
    }
  }

  async function fetchRateFrom(url, currency) {
    const response = await fetch(url, {
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) {
      throw new Error(`FX request failed: ${response.status}`);
    }

    const data = await response.json();
    const rate = Number(data?.eur?.[currency.toLowerCase()]);

    if (!Number.isFinite(rate) || rate <= 0) {
      throw new Error('Invalid FX rate');
    }

    return rate;
  }

  async function fetchRate(currency) {
    for (const url of FX_URLS) {
      try {
        return await fetchRateFrom(url, currency);
      } catch (error) {
        /* Try the fallback endpoint. */
      }
    }

    throw new Error('Unable to load FX rate');
  }

  function getContext() {
    if (contextPromise) return contextPromise;

    const country = getCountry();
    const currency = getCurrency(country);

    if (!country || !currency) {
      contextPromise = Promise.resolve(null);
      return contextPromise;
    }

    contextPromise = (async () => {
      const cachedRate = readCache(currency);

      if (cachedRate) {
        return { country, currency, rate: cachedRate };
      }

      try {
        const rate = await fetchRate(currency);
        writeCache(currency, rate);
        return { country, currency, rate };
      } catch (error) {
        return null;
      }
    })();

    return contextPromise;
  }

  function formatMoney(value, currency) {
    const number = numberFormatter.format(Math.round(value));

    switch (currency) {
      case 'GBP': return `£${number}`;
      case 'CHF': return `CHF ${number}`;
      case 'NOK': return `${number} kr`;
      case 'SEK': return `${number} kr`;
      case 'DKK': return `${number} kr`;
      case 'PLN': return `${number} zł`;
      case 'CZK': return `${number} Kč`;
      case 'HUF': return `${number} Ft`;
      case 'RON': return `${number} lei`;
      case 'AUD': return `A$${number}`;
      case 'NZD': return `NZ$${number}`;
      case 'USD': return `$${number}`;
      case 'CAD': return `C$${number}`;
      case 'GEL': return `${number} ₾`;
      default: return `${number} ${currency}`;
    }
  }

  function convertCents(cents, context) {
    return formatMoney((cents / 100) * context.rate, context.currency);
  }

  function getNativeEurPriceText(root) {
    const priceBlock = getProductPriceBlock(root);
    if (!priceBlock) return '';

    const salePrice = priceBlock.querySelector(
      '.price--on-sale .price-item--sale.price-item--last'
    );

    const regularPrice = priceBlock.querySelector(
      '.price__regular .price-item--regular'
    );

    const fallback = priceBlock.querySelector('.price-item');

    return String(
      salePrice?.textContent
      || regularPrice?.textContent
      || fallback?.textContent
      || ''
    ).replace(/\s+/g, ' ').trim();
  }

  function renderStickyPrice(root, context) {
    const stickyPrice = document.querySelector('[data-yy-sticky-price]');
    if (!stickyPrice) return;

    const cents = Number(root.dataset.eurCents);

    if (!Number.isFinite(cents) || cents < 0) {
      stickyPrice.classList.remove('is-local');
      stickyPrice.replaceChildren();
      return;
    }

    if (context) {
      const approx = document.createElement('span');
      approx.className = 'yy-sticky-product-bar__price-approx';
      approx.textContent = '≈';

      const amount = document.createElement('span');
      amount.className = 'yy-sticky-product-bar__price-amount';
      amount.textContent = convertCents(cents, context);

      stickyPrice.classList.add('is-local');
      stickyPrice.replaceChildren(approx, amount);
      return;
    }

    stickyPrice.classList.remove('is-local');
    stickyPrice.textContent = getNativeEurPriceText(root);
  }

  function hideCardPrice(element) {
    element.textContent = '';
    element.hidden = true;
  }

  function renderCardPrice(element, context) {
    const cents = Number(element.dataset.eurCents);

    if (!context || !Number.isFinite(cents) || cents < 0) {
      hideCardPrice(element);
      return;
    }

    const formatted = convertCents(cents, context);
    const varies = element.dataset.priceVaries === 'true';

    element.textContent = varies ? `≈ from ${formatted}` : `≈ ${formatted}`;
    element.hidden = false;
  }

  function getProductVariants(root) {
    if (variantsCache.has(root)) return variantsCache.get(root);

    let script = null;
    const siblingScript = root.nextElementSibling;

    if (siblingScript?.matches?.('[data-yy-local-price-variants]')) {
      script = siblingScript;
    }

    if (!script) {
      script =
        root.parentElement?.querySelector('[data-yy-local-price-variants]') ||
        root.closest('product-info')?.querySelector('[data-yy-local-price-variants]');
    }

    let variants = [];

    try {
      variants = script ? JSON.parse(script.textContent) : [];
      if (!Array.isArray(variants)) variants = [];
    } catch (error) {
      variants = [];
    }

    variantsCache.set(root, variants);
    return variants;
  }

  function findVariant(variants, id) {
    return variants.find(variant => String(variant.id) === String(id));
  }

  function getSelectedVariant(root, variants) {
    const productInfo = root.closest('product-info');
    const scope = productInfo || root.closest('.shopify-section') || document;

    const inputs = scope.querySelectorAll(
      'form[action*="/cart/add"] input[name="id"]'
    );

    for (const input of inputs) {
      const variant = findVariant(variants, input.value);
      if (variant) return variant;
    }

    const urlVariant = new URLSearchParams(window.location.search).get('variant');
    if (urlVariant) {
      const variant = findVariant(variants, urlVariant);
      if (variant) return variant;
    }

    return findVariant(variants, root.dataset.variantId);
  }

  function syncProductVariant(root) {
    const variants = getProductVariants(root);
    if (!variants.length) return;

    const variant = getSelectedVariant(root, variants);
    if (!variant) return;

    root.dataset.variantId = String(variant.id);
    root.dataset.eurCents = String(variant.price);
  }

  function getProductPriceBlock(root) {
    const productInfo = root.closest('product-info');
    if (!productInfo) return null;

    const sectionId = productInfo.dataset.section;

    if (sectionId) {
      const exact = document.getElementById(`price-${sectionId}`);
      if (exact) return exact;
    }

    return productInfo.querySelector('[id^="price-"]');
  }

  function getInlineProductPrice(root) {
    const priceBlock = getProductPriceBlock(root);
    if (!priceBlock) return null;

    const nativePrice = priceBlock.querySelector('.price');
    const priceContainer = nativePrice?.querySelector(':scope > .price__container');
    if (!nativePrice || !priceContainer) return null;

    let inline = priceBlock.querySelector('[data-yy-local-inline]');

    if (!inline) {
      inline = document.createElement('span');
      inline.className = 'yy-local-price-inline';
      inline.setAttribute('data-yy-local-inline', '');
      inline.hidden = true;
    }

    if (
      inline.parentElement !== nativePrice
      || inline.previousElementSibling !== priceContainer
    ) {
      priceContainer.insertAdjacentElement('afterend', inline);
    }

    return inline;
  }

  function renderProductPrice(root, context) {
    syncProductVariant(root);

    const cents = Number(root.dataset.eurCents);
    const inlineNode = getInlineProductPrice(root);

    renderStickyPrice(root, context);

    /* The legacy Currency product block remains only as a data source. */
    root.hidden = true;

    if (!inlineNode || !context || !Number.isFinite(cents) || cents < 0) {
      if (inlineNode) {
        inlineNode.textContent = '';
        inlineNode.hidden = true;
      }
      return;
    }

    const formatted = convertCents(cents, context);
    const nextText = `≈ ${formatted}`;

    if (inlineNode.textContent !== nextText) {
      inlineNode.textContent = nextText;
    }

    if (inlineNode.hidden) {
      inlineNode.hidden = false;
    }
  }

  function collectTargets(root, selector) {
    const result = [];

    if (root instanceof Element && root.matches(selector)) {
      result.push(root);
    }

    if (root && typeof root.querySelectorAll === 'function') {
      result.push(...root.querySelectorAll(selector));
    }

    return result;
  }

  function scan(root = document) {
    const cards = collectTargets(root, CARD_SELECTOR);
    const productPrices = collectTargets(root, PRODUCT_SELECTOR);

    if (!cards.length && !productPrices.length) return;

    getContext().then(context => {
      cards.forEach(element => renderCardPrice(element, context));
      productPrices.forEach(element => renderProductPrice(element, context));
    });
  }

  function containsTarget(node) {
    if (!(node instanceof Element || node instanceof DocumentFragment)) {
      return false;
    }

    if (node instanceof Element && node.matches(TARGET_SELECTOR)) return true;
    return Boolean(node.querySelector?.(TARGET_SELECTOR));
  }

  function queueScan(root = document) {
    if (queuedFrame) return;

    queuedFrame = requestAnimationFrame(() => {
      queuedFrame = null;
      scan(root);
    });
  }

  function mutationTouchesProductPrice(mutation) {
    const target = mutation.target;

    if (target instanceof Element) {
      if (
        target.matches('[id^="price-"], [id^="price-"] *')
        || target.closest?.('[id^="price-"]')
      ) {
        return true;
      }
    }

    for (const node of mutation.addedNodes) {
      if (!(node instanceof Element)) continue;

      if (
        node.matches?.('[id^="price-"], [id^="price-"] *')
        || node.querySelector?.('[id^="price-"]')
      ) {
        return true;
      }
    }

    return false;
  }

  function startObserver() {
    if (observer || !document.body) return;

    observer = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        if (mutationTouchesProductPrice(mutation)) {
          queueScan(document);
          return;
        }

        for (const node of mutation.addedNodes) {
          if (containsTarget(node)) {
            queueScan(document);
            return;
          }
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  function handleProductChange(event) {
    const productInfo = event.target?.closest?.('product-info');
    if (!productInfo) return;

    setTimeout(() => scan(productInfo), 0);
    setTimeout(() => scan(productInfo), 180);
  }

  function init() {
    scan(document);
    startObserver();

    document.addEventListener('change', handleProductChange);

    document.addEventListener('shopify:section:load', event => {
      scan(event.target);
    });

    if (
      typeof subscribe === 'function' &&
      typeof PUB_SUB_EVENTS !== 'undefined' &&
      PUB_SUB_EVENTS.variantChange
    ) {
      subscribe(PUB_SUB_EVENTS.variantChange, () => {
        setTimeout(() => queueScan(document), 0);
        setTimeout(() => queueScan(document), 180);
      });
    }
  }

  window.YYLocalCurrency = {
    initialized: true,
    refresh(root = document) {
      scan(root);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
